/**
 * Transaction Management Component
 * View and manage transactions for a company with filtering, pagination, and inline editing
 */
import { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';
import '../styles/TransactionManagement.css';

function TransactionManagement({ companyId, companyName }) {
  // Transaction state
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 50;

  // Filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [transactionType, setTransactionType] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Department state
  const [departments, setDepartments] = useState([]);
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState('');

  // Inline editing state
  const [editingId, setEditingId] = useState(null);
  const [editDepartment, setEditDepartment] = useState('');
  const [editComment, setEditComment] = useState('');

  // New department inline
  const [showNewDeptInput, setShowNewDeptInput] = useState(false);
  const [newDeptName, setNewDeptName] = useState('');

  // Sync state
  const [syncing, setSyncing] = useState(false);
  const [syncStartDate, setSyncStartDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().split('T')[0];
  });
  const [syncEndDate, setSyncEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Load data
  const loadTransactions = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
      });

      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (transactionType) params.append('transaction_type', transactionType);
      if (departmentFilter) {
        if (departmentFilter === 'none') {
          params.append('has_department', 'false');
        } else {
          params.append('department_id', departmentFilter);
        }
      }
      if (searchTerm) params.append('search', searchTerm);
      if (selectedBankAccount) params.append('account_name', selectedBankAccount);

      const response = await api.get(`/transactions/${companyId}?${params.toString()}`);
      setTransactions(response.data.transactions);
      setTotal(response.data.total);
      setTotalPages(response.data.total_pages);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  }, [companyId, page, startDate, endDate, transactionType, departmentFilter, searchTerm, selectedBankAccount]);

  const loadDepartments = useCallback(async () => {
    if (!companyId) return;

    try {
      const response = await api.get(`/departments/${companyId}`);
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to load departments:', error);
    }
  }, [companyId]);

  const loadTransactionTypes = useCallback(async () => {
    if (!companyId) return;

    try {
      const response = await api.get(`/transactions/${companyId}/types`);
      setTransactionTypes(response.data.transaction_types);
    } catch (error) {
      console.error('Failed to load transaction types:', error);
    }
  }, [companyId]);

  const loadBankAccounts = useCallback(async () => {
    if (!companyId) return;

    try {
      const response = await api.get(`/transactions/${companyId}/bank-accounts`);
      setBankAccounts(response.data.bank_accounts);
    } catch (error) {
      console.error('Failed to load bank accounts:', error);
    }
  }, [companyId]);

  useEffect(() => {
    if (companyId) {
      loadTransactions();
      loadDepartments();
      loadTransactionTypes();
      loadBankAccounts();
    }
  }, [companyId, loadTransactions, loadDepartments, loadTransactionTypes, loadBankAccounts]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [startDate, endDate, transactionType, departmentFilter, searchTerm, selectedBankAccount]);

  // Sync transactions from QuickBooks
  const handleSync = async () => {
    if (!companyId || !syncStartDate || !syncEndDate) {
      alert('Please select date range for sync');
      return;
    }

    if (!confirm(`Sync transactions for ${companyName} from ${syncStartDate} to ${syncEndDate}?\n\nThis will replace existing transactions for this period.`)) {
      return;
    }

    setSyncing(true);
    try {
      const response = await api.post(`/transactions/${companyId}/sync`, {
        start_date: syncStartDate,
        end_date: syncEndDate
      });

      alert(`Sync complete! ${response.data.transactions_synced} transactions synced.`);
      loadTransactions();
      loadTransactionTypes();
      loadBankAccounts();
    } catch (error) {
      alert('Sync failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSyncing(false);
    }
  };

  // Start inline editing
  const startEditing = (transaction) => {
    setEditingId(transaction.id);
    setEditDepartment(transaction.department_id || '');
    setEditComment(transaction.user_comment || '');
  };

  // Cancel inline editing
  const cancelEditing = () => {
    setEditingId(null);
    setEditDepartment('');
    setEditComment('');
  };

  // Save inline edit
  const saveEdit = async (transactionId) => {
    try {
      const updateData = {};
      if (editDepartment !== '') {
        updateData.department_id = parseInt(editDepartment);
      }
      updateData.user_comment = editComment;

      await api.put(`/transactions/${companyId}/${transactionId}`, updateData);

      // Update local state
      setTransactions(prev => prev.map(t =>
        t.id === transactionId
          ? {
              ...t,
              department_id: editDepartment ? parseInt(editDepartment) : null,
              department_name: departments.find(d => d.id === parseInt(editDepartment))?.name || null,
              user_comment: editComment
            }
          : t
      ));

      setEditingId(null);
    } catch (error) {
      alert('Failed to update: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Quick department update (without entering edit mode)
  const quickUpdateDepartment = async (transactionId, deptId) => {
    try {
      const updateData = { department_id: deptId ? parseInt(deptId) : null };
      await api.put(`/transactions/${companyId}/${transactionId}`, updateData);

      setTransactions(prev => prev.map(t =>
        t.id === transactionId
          ? {
              ...t,
              department_id: deptId ? parseInt(deptId) : null,
              department_name: departments.find(d => d.id === parseInt(deptId))?.name || null
            }
          : t
      ));
    } catch (error) {
      alert('Failed to update department: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Create new department inline
  const handleCreateDepartment = async () => {
    if (!newDeptName.trim()) return;

    try {
      const response = await api.post(`/departments/${companyId}`, {
        name: newDeptName.trim()
      });

      setDepartments(prev => [...prev, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewDeptName('');
      setShowNewDeptInput(false);
    } catch (error) {
      alert('Failed to create department: ' + (error.response?.data?.detail || error.message));
    }
  };

  const clearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTransactionType('');
    setDepartmentFilter('');
    setSearchTerm('');
    setSelectedBankAccount('');
    setPage(1);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return '-';
    const num = parseFloat(amount);
    const formatted = Math.abs(num).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD'
    });
    return num < 0 ? `-${formatted}` : formatted;
  };

  if (!companyId) {
    return <div className="transaction-management">Please select a company to view transactions.</div>;
  }

  return (
    <div className="transaction-management">
      {/* Header with Sync Controls */}
      <div className="transaction-header">
        <h2>Transactions: {companyName}</h2>
        <div className="sync-controls">
          <input
            type="date"
            value={syncStartDate}
            onChange={(e) => setSyncStartDate(e.target.value)}
            className="sync-date"
          />
          <span className="sync-to">to</span>
          <input
            type="date"
            value={syncEndDate}
            onChange={(e) => setSyncEndDate(e.target.value)}
            className="sync-date"
          />
          <button
            onClick={handleSync}
            disabled={syncing}
            className="btn-primary btn-sync"
          >
            {syncing ? 'Syncing...' : 'Sync from QuickBooks'}
          </button>
        </div>
      </div>

      {/* Department Management Row */}
      <div className="department-management-row">
        <span className="dept-label">Departments:</span>
        <div className="dept-chips">
          {departments.map(dept => (
            <span key={dept.id} className="dept-chip">{dept.name}</span>
          ))}
          {showNewDeptInput ? (
            <div className="new-dept-inline">
              <input
                type="text"
                value={newDeptName}
                onChange={(e) => setNewDeptName(e.target.value)}
                placeholder="New department name"
                className="new-dept-input"
                onKeyDown={(e) => e.key === 'Enter' && handleCreateDepartment()}
              />
              <button onClick={handleCreateDepartment} className="btn-small btn-add">Add</button>
              <button onClick={() => { setShowNewDeptInput(false); setNewDeptName(''); }} className="btn-small">Cancel</button>
            </div>
          ) : (
            <button onClick={() => setShowNewDeptInput(true)} className="btn-small btn-add-dept">+ Add</button>
          )}
        </div>
      </div>

      {/* Bank Account Filter Chips */}
      {bankAccounts.length > 0 && (
        <div className="bank-account-filter-row">
          <span className="filter-label">Bank Account:</span>
          <div className="bank-chips">
            <button
              className={`bank-chip ${selectedBankAccount === '' ? 'active' : ''}`}
              onClick={() => setSelectedBankAccount('')}
            >
              All ({bankAccounts.reduce((sum, acc) => sum + acc.count, 0)})
            </button>
            {bankAccounts.map(account => (
              <button
                key={account.name}
                className={`bank-chip ${selectedBankAccount === account.name ? 'active' : ''}`}
                onClick={() => setSelectedBankAccount(account.name)}
                title={account.name}
              >
                {account.name.length > 20 ? account.name.substring(0, 20) + '...' : account.name} ({account.count})
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="transaction-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>From</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>To</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <div className="filter-group">
            <label>Type</label>
            <select
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
            >
              <option value="">All Types</option>
              {transactionTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>Department</label>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="none">Uncategorized</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          </div>
          <div className="filter-group filter-search">
            <label>Search</label>
            <input
              type="text"
              placeholder="Search description, memo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-actions">
            <button onClick={clearFilters} className="btn-secondary btn-small">Clear</button>
          </div>
        </div>
      </div>

      {/* Results info */}
      <div className="transaction-info">
        {total} transactions
        {(startDate || endDate || transactionType || departmentFilter || searchTerm || selectedBankAccount) &&
          <span className="filter-active"> (filtered)</span>
        }
      </div>

      {/* Table */}
      {loading ? (
        <div className="loading">Loading transactions...</div>
      ) : (
        <div className="transaction-table-container">
          <table className="data-table transaction-table">
            <thead>
              <tr>
                <th style={{width: '90px'}}>Date</th>
                <th style={{width: '80px'}}>Type</th>
                <th style={{width: '100px'}}>Amount</th>
                <th style={{width: '150px'}}>Bank Account</th>
                <th className="desc-col">Bank Description</th>
                <th style={{width: '130px'}}>Department</th>
                <th style={{width: '150px'}}>Comment</th>
              </tr>
            </thead>
            <tbody>
              {transactions.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    No transactions found. Use "Sync from QuickBooks" to pull transactions.
                  </td>
                </tr>
              ) : (
                transactions.map(transaction => (
                  <tr key={transaction.id} className={editingId === transaction.id ? 'editing-row' : ''}>
                    <td>{formatDate(transaction.transaction_date)}</td>
                    <td>
                      <span className={`type-badge type-${transaction.transaction_type?.toLowerCase()}`}>
                        {transaction.transaction_type || '-'}
                      </span>
                    </td>
                    <td className={`amount ${parseFloat(transaction.amount) < 0 ? 'negative' : 'positive'}`}>
                      {formatAmount(transaction.amount)}
                    </td>
                    <td className="account-cell" title={transaction.account_name}>
                      {transaction.account_name || '-'}
                    </td>
                    <td className="desc-cell" title={transaction.bank_description || transaction.memo}>
                      {transaction.bank_description || transaction.memo || '-'}
                    </td>
                    <td className="dept-cell">
                      {editingId === transaction.id ? (
                        <select
                          value={editDepartment}
                          onChange={(e) => setEditDepartment(e.target.value)}
                          className="inline-select"
                        >
                          <option value="">None</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                          ))}
                        </select>
                      ) : (
                        <select
                          value={transaction.department_id || ''}
                          onChange={(e) => quickUpdateDepartment(transaction.id, e.target.value)}
                          className="inline-select"
                        >
                          <option value="">-</option>
                          {departments.map(dept => (
                            <option key={dept.id} value={dept.id}>{dept.name}</option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td className="comment-cell">
                      {editingId === transaction.id ? (
                        <div className="edit-comment-row">
                          <input
                            type="text"
                            value={editComment}
                            onChange={(e) => setEditComment(e.target.value)}
                            className="inline-input"
                            placeholder="Add comment..."
                            onKeyDown={(e) => e.key === 'Enter' && saveEdit(transaction.id)}
                          />
                          <button onClick={() => saveEdit(transaction.id)} className="btn-inline btn-save">Save</button>
                          <button onClick={cancelEditing} className="btn-inline btn-cancel">X</button>
                        </div>
                      ) : (
                        <div className="comment-display" onClick={() => startEditing(transaction)}>
                          {transaction.user_comment || <span className="add-comment">+ Add</span>}
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={() => setPage(1)}
            disabled={page === 1}
            className="btn-small"
          >
            First
          </button>
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="btn-small"
          >
            Prev
          </button>
          <span className="page-info">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="btn-small"
          >
            Next
          </button>
          <button
            onClick={() => setPage(totalPages)}
            disabled={page === totalPages}
            className="btn-small"
          >
            Last
          </button>
        </div>
      )}
    </div>
  );
}

export default TransactionManagement;
